import { Comment, CommentsReactions, OrganisationTag, UserPreview } from '../services';
import { OrderDirection, PaginationInfo, ReactionsCount } from '../shared';
import { Channel } from './channelsNew';
import { graphqlApiWithPersisted } from './graphqlApi';
import { InAppActiveMessage } from './inAppMessage';
import { NotificationDefault } from './notifications';
import { Story } from './story';
import { StoryCard, StoryCardType } from './storyCard';

export enum AnalyticChartInterval {
  AUTO = 'AUTO',
  DAY_OF_WEEK = 'DAY_OF_WEEK',
  HOUR_OF_DAY = 'HOUR_OF_DAY',
}

export enum AnalyticChartIntervalResponse {
  DAY = 'DAY',
  DAY_OF_WEEK = 'DAY_OF_WEEK',
  HOUR = 'HOUR',
  HOUR_OF_DAY = 'HOUR_OF_DAY',
  MONTH = 'MONTH',
}

export enum AnalyticChartPlatform {
  ANDROID = 'ANDROID',
  IOS = 'IOS',
  WEB = 'WEB',
}

export enum AnalyticContentListOrderBy {
  COMMENTS = 'COMMENTS',
  COMMENT_REACTIONS = 'COMMENT_REACTIONS',
  NOTIFICATIONS = 'NOTIFICATIONS',
  PLAYS = 'PLAYS',
  PLAY_DURATION = 'PLAY_DURATION',
  PLAY_DURATION_AVG = 'PLAY_DURATION_AVG',
  POSTED_TIME = 'POSTED_TIME',
  REACTIONS = 'REACTIONS',
  REACTIONS_NEGATIVE = 'REACTIONS_NEGATIVE',
  REACTIONS_POSITIVE = 'REACTIONS_POSITIVE',
  REACTION_ANGRY = 'REACTION_ANGRY',
  REACTION_HAHA = 'REACTION_HAHA',
  REACTION_LIKE = 'REACTION_LIKE',
  REACTION_LOVE = 'REACTION_LOVE',
  REACTION_SAD = 'REACTION_SAD',
  REACTION_WOW = 'REACTION_WOW',
  SHARES = 'SHARES',
  VIEWS = 'VIEWS',
  ENGAGEMENT = 'ENGAGEMENT',
  POST_VIDEO_PLAYS = 'POST_VIDEO_PLAYS',
  POST_AUDIO_PLAYS = 'POST_AUDIO_PLAYS',
  POST_LINK_CLICKS = 'POST_LINK_CLICKS',
}

export enum AnalyticContentTagListOrderBy {
  ENGAGEMENT = 'ENGAGEMENT',
  PUBLISHED = 'PUBLISHED',
  VIEWS = 'VIEWS',
}

export enum NotificationListOrderBy {
  DELIVERY_TIME = 'DELIVERY_TIME',
  OPEN = 'OPEN',
  SENT = 'SENT',
}

export enum AnalyticChartStoryListOrderBy {
  DURATION = 'DURATION',
  DURATION_AVG = 'DURATION_AVG',
  VIEWS = 'VIEWS',
}

interface FilterArgs {
  from: string;
  to: string;
  channelId?: number[];
  platform?: AnalyticChartPlatform[];
  categoryId?: number[];
  organisationTags?: {
    externalTagsId: (number | null)[];
    externalStatusTagsId?: (number | null)[];
    internalTagsId: (number | null)[];
  };
}

interface FilterArgsWithInterval extends FilterArgs {
  interval?: AnalyticChartInterval;
}

export interface TimeSeriesDataset {
  data: number[];
  label: string;
}

export interface ChartTimeSeries {
  datasets: TimeSeriesDataset[];
  interval: AnalyticChartIntervalResponse | null;
  intervals: string[];
  labels: string[];
}

interface AnalyticContentInteractionTableDataset {
  label: string;
  value: number;
  payload: { published: number; interacted: number };
}

interface AnalyticContentInteractionTableSeries {
  items: AnalyticContentInteractionTableDataset[];
  pageInfo: PaginationInfo;
}

interface ContentListArgs extends FilterArgs {
  storyCardType: StoryCardType[];
  orderBy: AnalyticContentListOrderBy;
  orderDirection: OrderDirection;
  page?: number;
  size?: number;
  query?: string;
}

interface ContentTagListArgs extends FilterArgs {
  orderBy: AnalyticContentTagListOrderBy;
  orderDirection: OrderDirection;
  page?: number;
  size?: number;
}

interface AnalyticContentListTableDatasetPayload {
  channel: Channel;
  commentReactions: CommentsReactions[];
  commentReactionsTotal: number;
  comments: number;
  notifications: number;
  reactions: ReactionsCount[];
  reactionsTotal: number;
  reactionsPositive: number;
  reactionsNegative: number;
  shares: number;
  storyCard: StoryCard;
  views: number;
  engagement: number;
  plays: number;
  playDuration: number;
  playDurationAvg: number;
  postAudioPlays: number;
  postLinkClicks: number;
  postVideoPlays: number;
}

export interface AnalyticContentListTableDataset {
  label: string;
  value: number;
  payload: AnalyticContentListTableDatasetPayload;
}

interface AnalyticContentListTableSeries {
  items: AnalyticContentListTableDataset[];
  pageInfo: PaginationInfo;
}

interface AnalyticContentTagListTableDatasetPayload {
  engagement: number;
  published: number;
  tag: OrganisationTag;
  views: number;
}

export interface AnalyticContentTagListTableDataset {
  label: string;
  value: number;
  payload: AnalyticContentTagListTableDatasetPayload;
}

interface AnalyticContentTagListTableSeries {
  items: AnalyticContentTagListTableDataset[];
  pageInfo: PaginationInfo;
}

interface CommentListArgs extends FilterArgs {
  orderDirection: OrderDirection;
  page?: number;
  size?: number;
}

interface AnalyticCommentListTableDatasetPayload {
  channel: Channel;
  comment: Comment;
  commentId: number;
  commentReactions: CommentsReactions[];
  commentReactionsTotal: number;
  storyCardTitle: string;
}

interface AnalyticCommentListTableDataset {
  label: string;
  value: number;
  payload: AnalyticCommentListTableDatasetPayload;
}

interface AnalyticCommentListTableSeries {
  items: AnalyticCommentListTableDataset[];
  pageInfo: PaginationInfo;
}

interface AnalyticChannelListTableDataset {
  label: string;
  payload: Channel;
  value: number;
}

interface AnalyticChannelListTableSeries {
  items: AnalyticChannelListTableDataset[];
  pageInfo: PaginationInfo;
}

interface AnalyticUserInteractionTableDataset {
  label: string;
  value: number;
}

interface AnalyticUserInteractionTableSeries {
  items: AnalyticUserInteractionTableDataset[];
  pageInfo: PaginationInfo;
}

interface AnalyticUserRoleContentTableDataset {
  label: string;
  value: number;
}

interface AnalyticUserRoleContentTableSeries {
  items: AnalyticUserRoleContentTableDataset[];
  pageInfo: PaginationInfo;
}

interface NotificationListArgs extends FilterArgs {
  orderBy: NotificationListOrderBy;
  orderDirection: OrderDirection;
  page?: number;
  size?: number;
  query?: string;
}

export interface AnalyticPushNotificationListTableDatasetPayload {
  channel: Channel | null;
  notification: NotificationDefault;
  open: number;
  sent: number;
}

interface AnalyticPushNotificationListTableDataset {
  label: string;
  value: number;
  payload: AnalyticPushNotificationListTableDatasetPayload;
}

export interface AnalyticPushNotificationListTableSeries {
  items: AnalyticPushNotificationListTableDataset[];
  pageInfo: PaginationInfo;
}

export interface AnalyticInAppMessageTableDatasetPayload {
  channel: Channel | null;
  inAppMessage: InAppActiveMessage;
  open: number;
  sent: number;
}

interface AnalyticInAppMessageListTableDataset {
  label: string;
  value: number;
  payload: AnalyticInAppMessageTableDatasetPayload;
}

export interface AnalyticInAppMessageListTableSeries {
  items: AnalyticInAppMessageListTableDataset[];
  pageInfo: PaginationInfo;
}

interface AnalyticUserFunnelTableDataset {
  label: string;
  value: number;
}

interface AnalyticUserFunnelTableSeries {
  items: AnalyticUserFunnelTableDataset[];
  pageInfo: PaginationInfo;
}

interface AnalyticStoryListTableDatasetPayload {
  channel: Channel;
  duration: number;
  durationAvg: number;
  story: Story;
  views: number;
}

interface AnalyticStoryListTableDataset {
  label: string;
  value: number;
  payload: AnalyticStoryListTableDatasetPayload;
}

interface AnalyticStoryListTableSeries {
  items: AnalyticStoryListTableDataset[];
  pageInfo: PaginationInfo;
}

interface StoryListArgs extends FilterArgs {
  orderBy: AnalyticChartStoryListOrderBy;
  orderDirection: OrderDirection;
  page?: number;
  size?: number;
}

interface AnalyticChartTableTotalDataset {
  label: string;
  value: number;
}

export interface AnalyticChartTableTotalSeries {
  items: AnalyticChartTableTotalDataset[];
}

interface AnalyticChartTableTotalDataset {
  label: string;
  value: number;
}

interface UserReferralListArgs extends FilterArgs {
  orderDirection: OrderDirection;
  page?: number;
  size?: number;
}

interface AnalyticUserReferralTableDatasetPayload {
  user: UserPreview;
}

interface AnalyticUserReferralListTableDataset {
  label: string;
  value: number;
  payload: AnalyticUserReferralTableDatasetPayload;
}

interface AnalyticUserReferralListTableSeries {
  items: AnalyticUserReferralListTableDataset[];
  pageInfo: PaginationInfo;
}

interface AnalyticChartsData {
  contentActivity: ChartTimeSeries;
  contentInteraction: AnalyticContentInteractionTableSeries;
  contentList: AnalyticContentListTableSeries;
  contentTagList: AnalyticContentTagListTableSeries;
  commentList: AnalyticCommentListTableSeries;
  channelListPosts: AnalyticChannelListTableSeries;
  channelListViews: AnalyticChannelListTableSeries;
  channelListReactions: AnalyticChannelListTableSeries;
  channelListComments: AnalyticChannelListTableSeries;
  channelListShares: AnalyticChannelListTableSeries;
  storyList: AnalyticStoryListTableSeries;
  storyTotal: AnalyticChartTableTotalSeries;
  pushNotificationList: AnalyticPushNotificationListTableSeries;
  pushNotificationTotal: AnalyticChartTableTotalSeries;
  inAppMessageList: AnalyticInAppMessageListTableSeries;
  inAppMessageTotal: AnalyticChartTableTotalSeries;
  userInteraction: AnalyticUserInteractionTableSeries;
  userRoleContentPublished: AnalyticUserRoleContentTableSeries;
  userRoleContentView: AnalyticUserRoleContentTableSeries;
  userRoleContentViewAvg: AnalyticUserRoleContentTableSeries;
  userRoleContentEngagement: AnalyticUserRoleContentTableSeries;
  userRoleContentEngagementAvg: AnalyticUserRoleContentTableSeries;
  userEngagement: ChartTimeSeries;
  userSession: ChartTimeSeries;
  userFunnel: AnalyticUserFunnelTableSeries;
  userFunnelV2: AnalyticUserFunnelTableSeries;
  userFunnelV3: AnalyticUserFunnelTableSeries;
  userDemoFunnel: AnalyticUserFunnelTableSeries;
  userLoginLogout: ChartTimeSeries;
  installUninstallApp: ChartTimeSeries;
  userActive: ChartTimeSeries;
  userActivity: ChartTimeSeries;
  userAllActivity: ChartTimeSeries;
  userDemoActivity: ChartTimeSeries;
  userReferralActivity: ChartTimeSeries;
  userReferralList: AnalyticUserReferralListTableSeries;
}

interface AnalyticChartsResponse {
  data: {
    analyticCharts: AnalyticChartsData;
  };
}

interface AnalyticGenerateContentCSVReportResponse {
  data: {
    analyticGenerateContentCSVReport: {
      payload: boolean;
    };
  };
}

interface AnalyticGenerateUserCSVReportArgs extends FilterArgs {
  yearUserSessionPerMonth: number;
}

interface AnalyticGenerateContentCSVReport extends FilterArgs {
  storyCardType: StoryCardType[];
}

interface AnalyticGenerateUserCSVReportResponse {
  data: {
    analyticGenerateUserCSVReport: {
      payload: boolean;
    };
  };
}

interface AnalyticGenerateOtherCSVReportResponse {
  data: {
    analyticGenerateOtherCSVReport: {
      payload: boolean;
    };
  };
}

interface AnalyticGenerateUserReferralCSVReportResponse {
  data: {
    analyticGenerateUserReferralCSVReport: {
      payload: boolean;
    };
  };
}

export const analyticsApi = graphqlApiWithPersisted.injectEndpoints({
  endpoints: (builder) => ({
    contentActivity: builder.query<ChartTimeSeries, FilterArgsWithInterval>({
      query: ({ channelId, from, to, interval, platform, categoryId, organisationTags }) => ({
        url: '/graphql/webapp?contentActivity',
        method: 'POST',
        body: {
          query: `query AnalyticCharts($interval: AnalyticChartPublicIntervalEnum, $filter: GetAnalyticContentFilterArgs!) {
            analyticCharts {
              contentActivity(interval: $interval, filter: $filter) {
                datasets {
                  data
                  label
                }
                interval
                intervals
                labels
              }
            }
          }`,
          variables: {
            interval,
            filter: { channelId, from, to, platform, categoryId, organisationTags },
          },
        },
      }),
      forceRefetch: () => true,
      transformResponse: (response: AnalyticChartsResponse) =>
        response.data.analyticCharts.contentActivity,
    }),
    contentInteraction: builder.query<AnalyticContentInteractionTableSeries, FilterArgs>({
      query: ({ channelId, from, to, platform, categoryId, organisationTags }) => ({
        url: '/graphql/webapp?contentInteraction',
        method: 'POST',
        body: {
          query: `query AnalyticCharts($filter: GetAnalyticContentFilterArgs!) {
            analyticCharts {
              contentInteraction(filter: $filter) {
                items {
                  label
                  value
                  payload {
                    published
                    interacted
                  }
                }
              }
            }
          }`,
          variables: {
            filter: { channelId, from, to, platform, categoryId, organisationTags },
          },
        },
      }),
      forceRefetch: () => true,
      transformResponse: (response: AnalyticChartsResponse) =>
        response.data.analyticCharts.contentInteraction,
    }),
    contentList: builder.query<AnalyticContentListTableSeries, ContentListArgs>({
      query: ({
        channelId,
        from,
        to,
        platform,
        categoryId,
        organisationTags,
        storyCardType,
        orderBy,
        orderDirection,
        page = 1,
        size = 15,
        query,
      }) => ({
        url: '/graphql/webapp?contentList',
        method: 'POST',
        body: {
          query: `query AnalyticCharts($page: Int, $size: Int, $orderBy: AnalyticContentListOrderByEnum!, $orderDirection: OrderDirectionEnum!, $filter: GetChartContentListFilterArgs!) {
            analyticCharts {
              contentList(page: $page, size: $size, orderBy: $orderBy, orderDirection: $orderDirection, filter: $filter) {
                items {
                  label
                  value
                  payload {
                    channel {
                      name
                      subdomain
                    }
                    commentReactions {
                      count
                      name
                    }
                    commentReactionsTotal
                    comments
                    notifications
                    reactions {
                      count
                      name
                    }
                    reactionsTotal
                    reactionsPositive
                    reactionsNegative
                    shares
                    storyCard {
                      id
                      type
                      postedAt
                      storyId
                      status
                      externalTags: organisationTags(filters: {types: [EXTERNAL]}) { name }
                    }
                    views
                    plays
                    playDuration
                    playDurationAvg
                    postAudioPlays
                    postLinkClicks
                    postVideoPlays
                  }
                }
                pageInfo {
                  page
                  perPage
                  hasNextPage
                  hasPreviousPage
                  totalItems
                  totalPages
                }
              }
            }
          }`,
          variables: {
            page,
            size,
            orderBy,
            orderDirection,
            filter: {
              channelId,
              from,
              to,
              platform,
              storyCardType,
              categoryId,
              organisationTags,
              query,
            },
          },
        },
      }),
      forceRefetch: () => true,
      transformResponse: (response: AnalyticChartsResponse) =>
        response.data.analyticCharts.contentList,
    }),
    contentTagList: builder.query<AnalyticContentTagListTableSeries, ContentTagListArgs>({
      query: ({
        channelId,
        from,
        to,
        platform,
        categoryId,
        organisationTags,
        orderBy,
        orderDirection,
        page = 1,
        size = 15,
      }) => ({
        url: '/graphql/webapp?contentTagList',
        method: 'POST',
        body: {
          query: `query AnalyticCharts($page: Int, $size: Int, $orderBy: AnalyticContentTagListOrderByEnum!, $orderDirection: OrderDirectionEnum!, $filter: GetChartContentTagListFilterArgs!) {
            analyticCharts {
              contentTagList(page: $page, size: $size, orderBy: $orderBy, orderDirection: $orderDirection, filter: $filter) {
                items {
                  label
                  value
                  payload {
                    engagement
                    published
                    tag {
                      id
                      type
                      name
                    }
                    views
                  }
                }
                pageInfo {
                  page
                  perPage
                  hasNextPage
                  hasPreviousPage
                  totalItems
                  totalPages
                }
              }
            }
          }`,
          variables: {
            page,
            size,
            orderBy,
            orderDirection,
            filter: { channelId, from, to, platform, categoryId, organisationTags },
          },
        },
      }),
      forceRefetch: () => true,
      transformResponse: (response: AnalyticChartsResponse) =>
        response.data.analyticCharts.contentTagList,
    }),
    commentList: builder.query<AnalyticCommentListTableSeries, CommentListArgs>({
      query: ({
        channelId,
        from,
        to,
        platform,
        categoryId,
        organisationTags,
        orderDirection,
        page = 1,
        size = 10,
      }) => ({
        url: '/graphql/webapp?commentList',
        method: 'POST',
        body: {
          query: `query AnalyticCharts($page: Int, $size: Int, $orderDirection: OrderDirectionEnum!, $filter: GetChartCommentListFilterArgs!) {
            analyticCharts {
              commentList(page: $page, size: $size, orderDirection: $orderDirection, filter: $filter) {
                items {
                  label
                  value
                  payload {
                    channel {
                      subdomain
                    }
                    comment {
                      id
                      content
                      author {
                        screenName
                        avatar {
                          averageColor
                          externalId
                          externalType
                          id
                          originalFilename
                          rightholder
                          status
                          statusCopyrightId
                          thumb
                          url
                        }
                      }
                      storyCard {
                        id
                        storyId
                        type
                        content {
                          ... on StoryCardThreadContent {
                            title
                          }
                          ... on StoryCardEditorialContent {
                            headline
                          }
                          ... on StoryCardImageContent {
                            headline
                          }
                          ... on StoryCardAudioContent {
                            headline
                          }
                          ... on StoryCardVideoContent {
                            headline
                          }
                          ... on StoryCardPdfContent {
                            headline
                          }
                          ... on StoryCardArticleContent {
                            title
                          }
                          ... on StoryCardPostContent {
                            title
                          }
                          ... on StoryCardQuoteContent {
                            quote
                          }
                          ... on StoryCardPollContent {
                            headline
                          }
                        }
                      }
                    }
                    commentReactionsTotal
                    storyCardTitle
                  }
                }
                pageInfo {
                  page
                  perPage
                  hasNextPage
                  hasPreviousPage
                }
              }
            }
          }`,
          variables: {
            page,
            size,
            orderDirection,
            filter: { channelId, from, to, platform, categoryId, organisationTags },
          },
        },
      }),
      forceRefetch: () => true,
      transformResponse: (response: AnalyticChartsResponse) =>
        response.data.analyticCharts.commentList,
    }),
    channelListPosts: builder.query<AnalyticChannelListTableSeries, FilterArgs>({
      query: ({ channelId, from, to, platform }) => ({
        url: '/graphql/webapp?channelListPosts',
        method: 'POST',
        body: {
          query: `query AnalyticCharts($filter: GetAnalyticFilterArgs!) {
            analyticCharts {
              channelListPosts(filter: $filter) {
                items {
                  label
                  value
                }
              }
            }
          }`,
          variables: {
            filter: { channelId, from, to, platform },
          },
        },
      }),
      forceRefetch: () => true,
      transformResponse: (response: AnalyticChartsResponse) =>
        response.data.analyticCharts.channelListPosts,
    }),
    channelListViews: builder.query<AnalyticChannelListTableSeries, FilterArgs>({
      query: ({ channelId, from, to, platform }) => ({
        url: '/graphql/webapp?channelListViews',
        method: 'POST',
        body: {
          query: `query AnalyticCharts($filter: GetAnalyticFilterArgs!) {
            analyticCharts {
              channelListViews(filter: $filter) {
                items {
                  label
                  value
                }
              }
            }
          }`,
          variables: {
            filter: { channelId, from, to, platform },
          },
        },
      }),
      forceRefetch: () => true,
      transformResponse: (response: AnalyticChartsResponse) =>
        response.data.analyticCharts.channelListViews,
    }),
    channelListReactions: builder.query<AnalyticChannelListTableSeries, FilterArgs>({
      query: ({ channelId, from, to, platform }) => ({
        url: '/graphql/webapp?channelListReactions',
        method: 'POST',
        body: {
          query: `query AnalyticCharts($filter: GetAnalyticFilterArgs!) {
            analyticCharts {
              channelListReactions(filter: $filter) {
                items {
                  label
                  value
                }
              }
            }
          }`,
          variables: {
            filter: { channelId, from, to, platform },
          },
        },
      }),
      forceRefetch: () => true,
      transformResponse: (response: AnalyticChartsResponse) =>
        response.data.analyticCharts.channelListReactions,
    }),
    channelListComments: builder.query<AnalyticChannelListTableSeries, FilterArgs>({
      query: ({ channelId, from, to, platform }) => ({
        url: '/graphql/webapp?channelListComments',
        method: 'POST',
        body: {
          query: `query AnalyticCharts($filter: GetAnalyticFilterArgs!) {
            analyticCharts {
              channelListComments(filter: $filter) {
                items {
                  label
                  value
                }
              }
            }
          }`,
          variables: {
            filter: { channelId, from, to, platform },
          },
        },
      }),
      forceRefetch: () => true,
      transformResponse: (response: AnalyticChartsResponse) =>
        response.data.analyticCharts.channelListComments,
    }),
    channelListShares: builder.query<AnalyticChannelListTableSeries, FilterArgs>({
      query: ({ channelId, from, to, platform }) => ({
        url: '/graphql/webapp?channelListShares',
        method: 'POST',
        body: {
          query: `query AnalyticCharts($filter: GetAnalyticFilterArgs!) {
            analyticCharts {
              channelListShares(filter: $filter) {
                items {
                  label
                  value
                }
              }
            }
          }`,
          variables: {
            filter: { channelId, from, to, platform },
          },
        },
      }),
      forceRefetch: () => true,
      transformResponse: (response: AnalyticChartsResponse) =>
        response.data.analyticCharts.channelListShares,
    }),
    storyList: builder.query<AnalyticStoryListTableSeries, StoryListArgs>({
      query: ({ channelId, from, to, platform, orderBy, orderDirection, page = 1, size = 10 }) => ({
        url: '/graphql/webapp?storyList',
        method: 'POST',
        body: {
          query: `query AnalyticCharts($page: Int, $size: Int, $orderBy: AnalyticChartStoryListOrderByEnum!, $orderDirection: OrderDirectionEnum!, $filter: GetAnalyticFilterArgs!) {
            analyticCharts {
              storyList(page: $page, size: $size, orderBy: $orderBy, orderDirection: $orderDirection, filter: $filter) {
                items {
                  label
                  payload {
                    channel {
                      subdomain
                    }
                    story {
                      id
                      title
                    }
                    duration
                    durationAvg
                    views
                  }
                }
                pageInfo {
                  page
                  perPage
                  hasNextPage
                  hasPreviousPage
                  totalItems
                  totalPages
                }
              }
            }
          }`,
          variables: {
            page,
            size,
            orderBy,
            orderDirection,
            filter: { channelId, from, to, platform },
          },
        },
      }),
      forceRefetch: () => true,
      transformResponse: (response: AnalyticChartsResponse) =>
        response.data.analyticCharts.storyList,
    }),
    storyTotal: builder.query<AnalyticChartTableTotalSeries, FilterArgs>({
      query: ({ channelId, from, to, platform }) => ({
        url: '/graphql/webapp?storyTotal',
        method: 'POST',
        body: {
          query: `query AnalyticCharts($filter: GetAnalyticFilterArgs!) {
            analyticCharts {
              storyTotal(filter: $filter) {
                items {
                  label
                  value
                }
              }
            }
          }`,
          variables: {
            filter: { channelId, from, to, platform },
          },
        },
      }),
      forceRefetch: () => true,
      transformResponse: (response: AnalyticChartsResponse) =>
        response.data.analyticCharts.storyTotal,
    }),
    pushNotificationList: builder.query<
      AnalyticPushNotificationListTableSeries,
      NotificationListArgs
    >({
      query: ({
        channelId,
        from,
        to,
        platform,
        orderBy,
        orderDirection,
        page = 1,
        size = 10,
        query,
      }) => ({
        url: '/graphql/webapp?pushNotificationList',
        method: 'POST',
        body: {
          query: `query AnalyticCharts($page: Int, $size: Int, $orderBy: NotificationListOrderByEnum!, $orderDirection: OrderDirectionEnum!, $filter: GetPushNotificationListChartTableFilterArgs!) {
            analyticCharts {
              pushNotificationList(page: $page, size: $size, orderBy: $orderBy, orderDirection: $orderDirection, filter: $filter) {
                items {
                  label
                  value
                  payload {
                    channel {
                      name
                      subdomain
                    }
                    notification {
                      deliveredTime
                      storyCard {
                        id
                        storyId
                      }
                      url
                    }
                    open
                    sent
                  }
                }
                pageInfo {
                  page
                  perPage
                  hasNextPage
                  hasPreviousPage
                  totalItems
                  totalPages
                }
              }
            }
          }`,
          variables: {
            page,
            size,
            orderBy,
            orderDirection,
            filter: { channelId, from, to, platform, query },
          },
        },
      }),
      forceRefetch: () => true,
      transformResponse: (response: AnalyticChartsResponse) =>
        response.data.analyticCharts.pushNotificationList,
    }),
    pushNotificationTotal: builder.query<AnalyticChartTableTotalSeries, FilterArgs>({
      query: ({ channelId, from, to, platform }) => ({
        url: '/graphql/webapp?pushNotificationTotal',
        method: 'POST',
        body: {
          query: `query AnalyticCharts($filter: GetAnalyticFilterArgs!) {
            analyticCharts {
              pushNotificationTotal(filter: $filter) {
                items {
                  label
                  value
                }
              }
            }
          }`,
          variables: {
            filter: { channelId, from, to, platform },
          },
        },
      }),
      forceRefetch: () => true,
      transformResponse: (response: AnalyticChartsResponse) =>
        response.data.analyticCharts.pushNotificationTotal,
    }),
    inAppMessageList: builder.query<AnalyticInAppMessageListTableSeries, NotificationListArgs>({
      query: ({
        channelId,
        from,
        to,
        platform,
        orderBy,
        orderDirection,
        page = 1,
        size = 10,
        query,
      }) => ({
        url: '/graphql/webapp?inAppMessageList',
        method: 'POST',
        body: {
          query: `query AnalyticCharts($page: Int, $size: Int, $orderBy: InAppMessageListOrderByEnum!, $orderDirection: OrderDirectionEnum!, $filter: GetInAppMessageListChartTableFilterArgs!) {
            analyticCharts {
              inAppMessageList(page: $page, size: $size, orderBy: $orderBy, orderDirection: $orderDirection, filter: $filter) {
                items {
                  label
                  value
                  payload {
                    channel {
                      name
                    }
                    inAppMessage {
                      title
                      delivery
                      primary {
                        action
                      }
                    }
                    open
                    sent
                  }
                }
                pageInfo {
                  page
                  perPage
                  hasNextPage
                  hasPreviousPage
                  totalItems
                  totalPages
                }
              }
            }
          }`,
          variables: {
            page,
            size,
            orderBy,
            orderDirection,
            filter: { channelId, from, to, platform, query },
          },
        },
      }),
      forceRefetch: () => true,
      transformResponse: (response: AnalyticChartsResponse) =>
        response.data.analyticCharts.inAppMessageList,
    }),
    inAppMessageTotal: builder.query<AnalyticChartTableTotalSeries, FilterArgs>({
      query: ({ from, to, channelId, platform }) => ({
        url: '/graphql/webapp?inAppMessageTotal',
        method: 'POST',
        body: {
          query: `query AnalyticCharts($filter: GetAnalyticFilterArgs!) {
            analyticCharts {
              inAppMessageTotal(filter: $filter) {
                items {
                  label
                  value
                }
              }
            }
          }`,
          variables: {
            filter: { channelId, from, to, platform },
          },
        },
      }),
      forceRefetch: () => true,
      transformResponse: (response: AnalyticChartsResponse) =>
        response.data.analyticCharts.inAppMessageTotal,
    }),
    userInteraction: builder.query<AnalyticUserInteractionTableSeries, FilterArgs>({
      query: ({ from, to, platform }) => ({
        url: '/graphql/webapp?userInteraction',
        method: 'POST',
        body: {
          query: `query AnalyticCharts($filter: GetAnalyticFilterArgs!) {
            analyticCharts {
              userInteraction(filter: $filter) {
                items {
                  label
                  value
                }
              }
            }
          }`,
          variables: {
            filter: { from, to, platform },
          },
        },
      }),
      forceRefetch: () => true,
      transformResponse: (response: AnalyticChartsResponse) =>
        response.data.analyticCharts.userInteraction,
    }),
    userRoleContentPublished: builder.query<AnalyticUserRoleContentTableSeries, FilterArgs>({
      query: ({ from, to, platform }) => ({
        url: '/graphql/webapp?userRoleContentPublished',
        method: 'POST',
        body: {
          query: `query AnalyticCharts($filter: GetAnalyticFilterArgs!) {
            analyticCharts {
              userRoleContentPublished(filter: $filter) {
                items {
                  label
                  value
                }
              }
            }
          }`,
          variables: {
            filter: { from, to, platform },
          },
        },
      }),
      forceRefetch: () => true,
      transformResponse: (response: AnalyticChartsResponse) =>
        response.data.analyticCharts.userRoleContentPublished,
    }),
    userRoleContentView: builder.query<AnalyticUserRoleContentTableSeries, FilterArgs>({
      query: ({ from, to, platform }) => ({
        url: '/graphql/webapp?userRoleContentView',
        method: 'POST',
        body: {
          query: `query AnalyticCharts($filter: GetAnalyticFilterArgs!) {
            analyticCharts {
              userRoleContentView(filter: $filter) {
                items {
                  label
                  value
                }
              }
            }
          }`,
          variables: {
            filter: { from, to, platform },
          },
        },
      }),
      forceRefetch: () => true,
      transformResponse: (response: AnalyticChartsResponse) =>
        response.data.analyticCharts.userRoleContentView,
    }),
    userRoleContentViewAvg: builder.query<AnalyticUserRoleContentTableSeries, FilterArgs>({
      query: ({ from, to, platform }) => ({
        url: '/graphql/webapp?userRoleContentViewAvg',
        method: 'POST',
        body: {
          query: `query AnalyticCharts($filter: GetAnalyticFilterArgs!) {
            analyticCharts {
              userRoleContentViewAvg(filter: $filter) {
                items {
                  label
                  value
                }
              }
            }
          }`,
          variables: {
            filter: { from, to, platform },
          },
        },
      }),
      forceRefetch: () => true,
      transformResponse: (response: AnalyticChartsResponse) =>
        response.data.analyticCharts.userRoleContentViewAvg,
    }),
    userRoleContentEngagement: builder.query<AnalyticUserRoleContentTableSeries, FilterArgs>({
      query: ({ from, to, platform }) => ({
        url: '/graphql/webapp?userRoleContentEngagement',
        method: 'POST',
        body: {
          query: `query AnalyticCharts($filter: GetAnalyticFilterArgs!) {
            analyticCharts {
              userRoleContentEngagement(filter: $filter) {
                items {
                  label
                  value
                }
              }
            }
          }`,
          variables: {
            filter: { from, to, platform },
          },
        },
      }),
      forceRefetch: () => true,
      transformResponse: (response: AnalyticChartsResponse) =>
        response.data.analyticCharts.userRoleContentEngagement,
    }),
    userRoleContentEngagementAvg: builder.query<AnalyticUserRoleContentTableSeries, FilterArgs>({
      query: ({ from, to, platform }) => ({
        url: '/graphql/webapp?userRoleContentEngagementAvg',
        method: 'POST',
        body: {
          query: `query AnalyticCharts($filter: GetAnalyticFilterArgs!) {
            analyticCharts {
              userRoleContentEngagementAvg(filter: $filter) {
                items {
                  label
                  value
                }
              }
            }
          }`,
          variables: {
            filter: { from, to, platform },
          },
        },
      }),
      forceRefetch: () => true,
      transformResponse: (response: AnalyticChartsResponse) =>
        response.data.analyticCharts.userRoleContentEngagementAvg,
    }),
    userEngagement: builder.query<ChartTimeSeries, FilterArgsWithInterval>({
      query: ({ from, to, interval, platform }) => ({
        url: '/graphql/webapp?userEngagement',
        method: 'POST',
        body: {
          query: `query AnalyticCharts($interval: AnalyticChartPublicIntervalEnum, $filter: GetAnalyticFilterArgs!) {
            analyticCharts {
              userEngagement(interval: $interval, filter: $filter) {
                datasets {
                  data
                  label
                }
                interval
                intervals
                labels
              }
            }
          }`,
          variables: {
            interval,
            filter: { from, to, platform },
          },
        },
      }),
      forceRefetch: () => true,
      transformResponse: (response: AnalyticChartsResponse) =>
        response.data.analyticCharts.userEngagement,
    }),
    userSession: builder.query<ChartTimeSeries, FilterArgsWithInterval>({
      query: ({ from, to, interval, platform }) => ({
        url: '/graphql/webapp?userSession',
        method: 'POST',
        body: {
          query: `query AnalyticCharts($interval: AnalyticChartPublicIntervalEnum, $filter: GetAnalyticFilterArgs!) {
            analyticCharts {
              userSession(interval: $interval, filter: $filter) {
                datasets {
                  data
                  label
                }
                interval
                intervals
              }
            }
          }`,
          variables: {
            interval,
            filter: { from, to, platform },
          },
        },
      }),
      forceRefetch: () => true,
      transformResponse: (response: AnalyticChartsResponse) =>
        response.data.analyticCharts.userSession,
    }),
    userFunnel: builder.query<AnalyticUserFunnelTableSeries, FilterArgs>({
      query: ({ from, to, platform }) => ({
        url: '/graphql/webapp?userFunnel',
        method: 'POST',
        body: {
          query: `query AnalyticCharts($filter: GetAnalyticFilterArgs!) {
            analyticCharts {
              userFunnel(filter: $filter) {
                items {
                  label
                  value
                }
              }
            }
          }`,
          variables: {
            filter: { from, to, platform },
          },
        },
      }),
      forceRefetch: () => true,
      transformResponse: (response: AnalyticChartsResponse) =>
        response.data.analyticCharts.userFunnel,
    }),
    userFunnelV2: builder.query<AnalyticUserFunnelTableSeries, FilterArgs>({
      query: ({ from, to, platform }) => ({
        url: '/graphql/webapp?userFunnelV2',
        method: 'POST',
        body: {
          query: `query AnalyticCharts($filter: GetAnalyticFilterArgs!) {
            analyticCharts {
              userFunnelV2(filter: $filter) {
                items {
                  label
                  value
                }
              }
            }
          }`,
          variables: {
            filter: { from, to, platform },
          },
        },
      }),
      forceRefetch: () => true,
      transformResponse: (response: AnalyticChartsResponse) =>
        response.data.analyticCharts.userFunnelV2,
    }),
    userFunnelV3: builder.query<AnalyticUserFunnelTableSeries, FilterArgs>({
      query: ({ from, to, platform }) => ({
        url: '/graphql/webapp?userFunnelV3',
        method: 'POST',
        body: {
          query: `query AnalyticCharts($filter: GetAnalyticFilterArgs!) {
            analyticCharts {
              userFunnelV3(filter: $filter) {
                items {
                  label
                  value
                }
              }
            }
          }`,
          variables: {
            filter: { from, to, platform },
          },
        },
      }),
      forceRefetch: () => true,
      transformResponse: (response: AnalyticChartsResponse) =>
        response.data.analyticCharts.userFunnelV3,
    }),
    userDemoFunnel: builder.query<AnalyticUserFunnelTableSeries, FilterArgs>({
      query: ({ from, to, platform }) => ({
        url: '/graphql/webapp?userDemoFunnel',
        method: 'POST',
        body: {
          query: `query AnalyticCharts($filter: GetAnalyticFilterArgs!) {
            analyticCharts {
              userDemoFunnel(filter: $filter) {
                items {
                  label
                  value
                }
              }
            }
          }`,
          variables: {
            filter: { from, to, platform },
          },
        },
      }),
      forceRefetch: () => true,
      transformResponse: (response: AnalyticChartsResponse) =>
        response.data.analyticCharts.userDemoFunnel,
    }),
    userLoginLogout: builder.query<ChartTimeSeries, FilterArgsWithInterval>({
      query: ({ from, to, interval }) => ({
        url: '/graphql/webapp?userLoginLogout',
        method: 'POST',
        body: {
          query: `query AnalyticCharts($interval: AnalyticChartPublicIntervalEnum, $filter: GetAnalyticFilterArgs!) {
            analyticCharts {
              userLoginLogout(interval: $interval, filter: $filter) {
                datasets {
                  data
                  label
                }
                interval
                intervals
                labels
              }
            }
          }`,
          variables: { interval, filter: { from, to } },
        },
      }),
      forceRefetch: () => true,
      transformResponse: (response: AnalyticChartsResponse) =>
        response.data.analyticCharts.userLoginLogout,
    }),
    installUninstallApp: builder.query<ChartTimeSeries, FilterArgsWithInterval>({
      query: ({ from, to, interval }) => ({
        url: '/graphql/webapp?installUninstallApp',
        method: 'POST',
        body: {
          query: `query AnalyticCharts($interval: AnalyticChartPublicIntervalEnum, $filter: GetAnalyticFilterArgs!) {
            analyticCharts {
              installUninstallApp(interval: $interval, filter: $filter) {
                datasets {
                  data
                  label
                }
                interval
                intervals
                labels
              }
            }
          }`,
          variables: { interval, filter: { from, to } },
        },
      }),
      forceRefetch: () => true,
      transformResponse: (response: AnalyticChartsResponse) =>
        response.data.analyticCharts.installUninstallApp,
    }),
    userActive: builder.query<ChartTimeSeries, FilterArgsWithInterval>({
      query: ({ from, to, interval }) => ({
        url: '/graphql/webapp?userActive',
        method: 'POST',
        body: {
          query: `query AnalyticCharts($interval: AnalyticChartPublicIntervalEnum, $filter: GetAnalyticFilterArgs!) {
            analyticCharts {
              userActive(interval: $interval, filter: $filter) {
                datasets {
                  data
                  label
                }
                interval
                intervals
                labels
              }
            }
          }`,
          variables: { interval, filter: { from, to } },
        },
      }),
      forceRefetch: () => true,
      transformResponse: (response: AnalyticChartsResponse) =>
        response.data.analyticCharts.userActive,
    }),
    userActivity: builder.query<ChartTimeSeries, FilterArgsWithInterval>({
      query: ({ from, to, platform, interval }) => ({
        url: '/graphql/webapp?userActivity',
        method: 'POST',
        body: {
          query: `query AnalyticCharts($interval: AnalyticChartPublicIntervalEnum, $filter: GetAnalyticFilterArgs!) {
            analyticCharts {
              userActivity(interval: $interval, filter: $filter) {
                datasets {
                  data
                  label
                }
                interval
                intervals
                labels
              }
            }
          }`,
          variables: {
            interval,
            filter: { from, to, platform },
          },
        },
      }),
      forceRefetch: () => true,
      transformResponse: (response: AnalyticChartsResponse) =>
        response.data.analyticCharts.userActivity,
    }),
    userAllActivity: builder.query<ChartTimeSeries, FilterArgsWithInterval>({
      query: ({ from, to, platform, interval }) => ({
        url: '/graphql/webapp?userAllActivity',
        method: 'POST',
        body: {
          query: `query AnalyticCharts($interval: AnalyticChartPublicIntervalEnum, $filter: GetAnalyticFilterArgs!) {
            analyticCharts {
              userAllActivity(interval: $interval, filter: $filter) {
                datasets {
                  data
                  label
                }
                interval
                intervals
                labels
              }
            }
          }`,
          variables: { interval, filter: { from, to, platform } },
        },
      }),
      forceRefetch: () => true,
      transformResponse: (response: AnalyticChartsResponse) =>
        response.data.analyticCharts.userAllActivity,
    }),
    userDemoActivity: builder.query<ChartTimeSeries, FilterArgsWithInterval>({
      query: ({ from, to, platform, interval }) => ({
        url: '/graphql/webapp?userDemoActivity',
        method: 'POST',
        body: {
          query: `query AnalyticCharts($interval: AnalyticChartPublicIntervalEnum, $filter: GetAnalyticFilterArgs!) {
            analyticCharts {
              userDemoActivity(interval: $interval, filter: $filter) {
                datasets {
                  data
                  label
                }
                interval
                intervals
                labels
              }
            }
          }`,
          variables: { interval, filter: { from, to, platform } },
        },
      }),
      forceRefetch: () => true,
      transformResponse: (response: AnalyticChartsResponse) =>
        response.data.analyticCharts.userDemoActivity,
    }),
    analyticGenerateContentCSVReport: builder.mutation<boolean, AnalyticGenerateContentCSVReport>({
      query: ({ from, to, channelId, platform, categoryId, organisationTags, storyCardType }) => ({
        url: '/graphql/webapp?analyticGenerateContentCSVReport',
        method: 'POST',
        body: {
          query: `mutation AnalyticGenerateContentCSVReport($input: AnalyticGenerateContentCsvReportInput!) {
            analyticGenerateContentCSVReport(input: $input) {
              payload
            }
          }`,
          variables: {
            input: {
              from,
              to,
              channelId,
              platform,
              categoryId,
              organisationTags,
              preferences: { storyCardType },
            },
          },
        },
      }),
      transformResponse: (response: AnalyticGenerateContentCSVReportResponse) =>
        response.data.analyticGenerateContentCSVReport.payload,
    }),
    analyticGenerateUserCSVReport: builder.mutation<boolean, AnalyticGenerateUserCSVReportArgs>({
      query: ({ from, to, channelId, platform, yearUserSessionPerMonth }) => ({
        url: '/graphql/webapp?analyticGenerateUserCSVReport',
        method: 'POST',
        body: {
          query: `mutation AnalyticGenerateUserCSVReport($input: AnalyticGenerateUserCsvReportInput!) {
            analyticGenerateUserCSVReport(input: $input) {
              payload
            }
          }`,
          variables: {
            input: {
              from,
              to,
              channelId,
              platform,
              preferences: { yearUserSessionPerMonth },
            },
          },
        },
      }),
      transformResponse: (response: AnalyticGenerateUserCSVReportResponse) =>
        response.data.analyticGenerateUserCSVReport.payload,
    }),
    analyticGenerateUserReferralCSVReport: builder.mutation<boolean, FilterArgs>({
      query: ({ from, to, channelId, platform }) => ({
        url: '/graphql/webapp?analyticGenerateUserReferralCSVReport',
        method: 'POST',
        body: {
          query: `mutation AnalyticGenerateUserReferralCSVReport($input: AnalyticGenerateCsvReportInput!){
            analyticGenerateUserReferralCSVReport(input: $input) {
              payload
            }
          }`,
          variables: {
            input: { from, to, channelId, platform },
          },
        },
      }),
      transformResponse: (response: AnalyticGenerateUserReferralCSVReportResponse) =>
        response.data.analyticGenerateUserReferralCSVReport.payload,
    }),
    analyticGenerateOtherCSVReport: builder.mutation<boolean, FilterArgs>({
      query: ({ from, to, channelId, platform }) => ({
        url: '/graphql/webapp?analyticGenerateOtherCSVReport',
        method: 'POST',
        body: {
          query: `mutation AnalyticGenerateOtherCSVReport($input: AnalyticGenerateCsvReportInput!){
            analyticGenerateOtherCSVReport(input: $input) {
              payload
            }
          }`,
          variables: {
            input: { from, to, channelId, platform },
          },
        },
      }),
      transformResponse: (response: AnalyticGenerateOtherCSVReportResponse) =>
        response.data.analyticGenerateOtherCSVReport.payload,
    }),
    userReferralActivity: builder.query<ChartTimeSeries, FilterArgsWithInterval>({
      query: ({ from, to, interval }) => ({
        url: '/graphql/webapp?userReferralActivity',
        method: 'POST',
        body: {
          query: `query AnalyticCharts($interval: AnalyticChartPublicIntervalEnum, $filter: GetAnalyticFilterArgs!) {
            analyticCharts {
              userReferralActivity(interval: $interval, filter: $filter) {
                datasets {
                  data
                  label
                }
                interval
                intervals
                labels
              }
            }
          }`,
          variables: { interval, filter: { from, to } },
        },
      }),
      forceRefetch: () => true,
      transformResponse: (response: AnalyticChartsResponse) =>
        response.data.analyticCharts.userReferralActivity,
    }),
    userReferralList: builder.query<AnalyticUserReferralListTableSeries, UserReferralListArgs>({
      query: ({ from, to, orderDirection, page = 1, size = 15 }) => ({
        url: '/graphql/webapp?userReferralList',
        method: 'POST',
        body: {
          query: `query AnalyticCharts($page: Int, $size: Int, $orderDirection: OrderDirectionEnum!, $filter: GetAnalyticFilterArgs!) {
            analyticCharts {
              userReferralList(page: $page, size: $size, orderDirection: $orderDirection, filter: $filter) {
                items {
                  label
                  value
                  payload {
                    user {
                      screenName
                    }
                  }
                }
                pageInfo {
                  page
                  perPage
                  hasNextPage
                  hasPreviousPage
                  totalItems
                  totalPages
                }
              }
            }
          }`,
          variables: {
            page,
            size,
            orderDirection,
            filter: { from, to },
          },
        },
      }),
      forceRefetch: () => true,
      transformResponse: (response: AnalyticChartsResponse) =>
        response.data.analyticCharts.userReferralList,
    }),
  }),
});

export const {
  useContentActivityQuery,
  useContentInteractionQuery,
  useContentListQuery,
  useContentTagListQuery,
  useCommentListQuery,
  useChannelListPostsQuery,
  useChannelListViewsQuery,
  useChannelListReactionsQuery,
  useChannelListCommentsQuery,
  useChannelListSharesQuery,
  useStoryListQuery,
  useStoryTotalQuery,
  usePushNotificationListQuery,
  usePushNotificationTotalQuery,
  useInAppMessageListQuery,
  useInAppMessageTotalQuery,
  useUserInteractionQuery,
  useUserRoleContentPublishedQuery,
  useUserRoleContentViewQuery,
  useUserRoleContentViewAvgQuery,
  useUserRoleContentEngagementQuery,
  useUserRoleContentEngagementAvgQuery,
  useUserEngagementQuery,
  useUserSessionQuery,
  useUserFunnelQuery,
  useUserFunnelV2Query,
  useUserFunnelV3Query,
  useUserDemoFunnelQuery,
  useUserLoginLogoutQuery,
  useInstallUninstallAppQuery,
  useUserActiveQuery,
  useUserActivityQuery,
  useUserAllActivityQuery,
  useUserDemoActivityQuery,
  useUserReferralActivityQuery,
  useUserReferralListQuery,
} = analyticsApi;
